import { useContext, useEffect } from 'react';
import { Box, Chip, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../components/shared/useUser';
import { StoreScope } from './StoreScopePicker';

interface ScopeChipsProps {
  /** Current scope from the picker; used to reflect which chip is active. */
  scope: StoreScope | null;
  onSelect: (scope: StoreScope) => void;
}

/**
 * Quick scope shortcuts for the common cases: the user's current store, or their whole current
 * franchise. Picking one sets the shared report scope directly. Full tenant/franchise/store
 * pickers remain available under Advanced filters for anything else.
 *
 * The franchise chip is hidden when the user only belongs to one franchise (nothing to toggle), and
 * the current store is auto-selected when there is only one store.
 */
const ScopeChips = ({ scope, onSelect }: ScopeChipsProps) => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const { user, selectedStore, selectedFranchise } = useContext(UserContext);

  const accessibleFranchiseCount = user?.franchises?.filter((f) => f.active !== false).length ?? 0;
  const accessibleStoreCount = user?.stores?.length ?? 0;

  // Current store scope: the logged-in store only.
  const currentStoreScope = (): StoreScope | null => {
    const storeNumber = selectedStore?.storeNumber;
    const franchiseId = selectedStore?.franchiseId ?? selectedFranchise?.franchiseId;
    const tenantId =
      selectedFranchise?.tenantId ??
      user?.franchises?.find((f) => f.franchiseId === franchiseId)?.tenantId;
    if (!storeNumber || franchiseId == null || tenantId == null) {
      return null;
    }
    return { tenantId, franchiseId, storeNumbers: [storeNumber] };
  };

  // Current franchise scope: all stores of the logged-in franchise.
  const currentFranchiseScope = (): StoreScope | null => {
    const franchiseId = selectedStore?.franchiseId ?? selectedFranchise?.franchiseId;
    const tenantId =
      selectedFranchise?.tenantId ??
      user?.franchises?.find((f) => f.franchiseId === franchiseId)?.tenantId;
    if (franchiseId == null || tenantId == null) {
      return null;
    }
    return { tenantId, franchiseId, storeNumbers: [] };
  };

  const store = currentStoreScope();
  const franchise = currentFranchiseScope();

  const isStoreActive = !!store && !!scope && scope.storeNumbers.length === 1 && scope.storeNumbers[0] === store.storeNumbers[0];
  const isFranchiseActive = !!franchise && !!scope && scope.franchiseId === franchise.franchiseId && scope.storeNumbers.length === 0;

  // Single-store users have nothing to choose — default to that store.
  useEffect(() => {
    if (accessibleStoreCount === 1 && store && !isStoreActive) {
      onSelect(store);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessibleStoreCount]);

  // Match the quick date-preset chips: default size, filled/outlined, primary when active.
  const chipSx = (active: boolean) => ({
    fontWeight: active ? 600 : 400,
    bgcolor: active ? theme.palette.primary.main : 'transparent',
    color: active ? theme.palette.primary.contrastText : theme.palette.text.primary,
    borderColor: active ? theme.palette.primary.main : theme.palette.custom.gray[300],
    '&:hover': { bgcolor: active ? theme.palette.primary.dark : theme.palette.custom.gray[100] },
  });

  return (
    <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
      {franchise && accessibleFranchiseCount > 1 && (
        <Chip
          label={t('reportingV2.current-franchise')}
          clickable
          onClick={() => onSelect(franchise)}
          variant={isFranchiseActive ? 'filled' : 'outlined'}
          aria-pressed={isFranchiseActive}
          sx={chipSx(isFranchiseActive)}
        />
      )}
      {store && (
        <Chip
          label={t('reportingV2.current-store')}
          clickable
          onClick={() => onSelect(store)}
          variant={isStoreActive ? 'filled' : 'outlined'}
          aria-pressed={isStoreActive}
          sx={chipSx(isStoreActive)}
        />
      )}
    </Box>
  );
};

export default ScopeChips;
